/**
 * useAudioTrackPlayer - Manages playback of audio tracks on the timeline
 * Uses Web Audio API for proper pan and volume control
 */
import { useRef, useEffect, useCallback, useState } from 'react';
import { getEffectiveDuration } from '../utils/clipUtils';

// Convert dB to linear gain (0 dB = 1.0, -6 dB ≈ 0.5, -inf dB = 0)
export const dbToGain = (db) => {
  if (db <= -60) return 0;
  return Math.pow(10, db / 20);
};

// Convert linear gain to dB
export const gainToDb = (gain) => {
  if (gain <= 0) return -60;
  return 20 * Math.log10(gain);
};

// Audio node structure for each clip
class AudioClipNode {
  constructor(audioContext, audioUrl) {
    this.audioContext = audioContext;
    this.audioUrl = audioUrl;
    this.audioBuffer = null;
    this.sourceNode = null;
    this.gainNode = audioContext.createGain();
    this.panNode = audioContext.createStereoPanner();
    this.isPlaying = false;
    this.startTime = 0;
    this.offset = 0;
    this.loadingPromise = null;
    
    // Connect nodes: source -> gain -> pan -> destination
    this.gainNode.connect(this.panNode);
    this.panNode.connect(audioContext.destination);
  }
  
  async loadAudio() {
    if (this.audioBuffer) return true;
    
    // Prevent multiple concurrent loads
    if (this.loadingPromise) {
      return this.loadingPromise;
    }
    
    this.loadingPromise = (async () => {
      try {
        const response = await fetch(this.audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        return true;
      } catch (err) {
        console.error('Failed to load audio:', err);
        return false;
      }
    })();
    
    return this.loadingPromise;
  }
  
  setVolume(gainDb) {
    this.gainNode.gain.setValueAtTime(dbToGain(gainDb), this.audioContext.currentTime);
  }
  
  setPan(pan) {
    // pan: -1 (left) to 1 (right)
    this.panNode.pan.setValueAtTime(Math.max(-1, Math.min(1, pan)), this.audioContext.currentTime);
  }
  
  play(offset = 0) {
    if (!this.audioBuffer) return;
    
    // Stop any existing playback first
    this.stop();
    
    this.sourceNode = this.audioContext.createBufferSource();
    this.sourceNode.buffer = this.audioBuffer;
    this.sourceNode.connect(this.gainNode);
    
    this.offset = offset;
    this.startTime = this.audioContext.currentTime;
    this.sourceNode.start(0, offset);
    this.isPlaying = true;
    
    this.sourceNode.onended = () => {
      this.isPlaying = false;
      this.sourceNode = null;
    };
  }
  
  stop() {
    if (this.sourceNode) {
      try {
        this.sourceNode.stop();
        this.sourceNode.disconnect();
      } catch (e) {
        // Ignore errors if already stopped
      }
      this.sourceNode = null;
    }
    this.isPlaying = false;
  }
  
  getCurrentTime() {
    if (!this.isPlaying) return this.offset;
    return this.offset + (this.audioContext.currentTime - this.startTime);
  }
  
  disconnect() {
    this.stop();
    this.gainNode.disconnect();
    this.panNode.disconnect();
  }
}

export function useAudioTrackPlayer({
  audioTracks,
  audioAssets,
  isPlaying,
  playheadPosition,
  getAssetUrl,
  positionRef  // Real-time position ref for sync during playback
}) {
  // Audio context ref
  const audioContextRef = useRef(null);
  // Map of clip ID -> AudioClipNode
  const audioNodesRef = useRef(new Map());
  // Track which clips are currently playing
  const playingClipsRef = useRef(new Set());
  // Interval for position sync
  const syncIntervalRef = useRef(null);
  // Flag to track if we should be playing (prevents race conditions)
  const shouldPlayRef = useRef(false);
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  // Local fallback ref for position if positionRef not provided
  const localPositionRef = useRef(0);

  // Initialize audio context
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browser autoplay policy)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Get the audio URL for a clip
  const getAudioUrlForClip = useCallback((clip) => {
    if (clip.asset_url) {
      return getAssetUrl ? getAssetUrl(clip.asset_url) : clip.asset_url;
    }
    if (!clip.asset_id || !audioAssets?.length) return null;
    const asset = audioAssets.find(a => a.id === clip.asset_id);
    if (!asset?.file_url) return null;
    return getAssetUrl ? getAssetUrl(asset.file_url) : asset.file_url;
  }, [audioAssets, getAssetUrl]);

  // Preload audio for clips (call this to avoid delay during playback)
  const preloadAudioClips = useCallback(async () => {
    if (!audioTracks?.length) return;
    
    const clipsToLoad = [];
    for (const track of audioTracks) {
      for (const clip of track.clips || []) {
        const audioUrl = getAudioUrlForClip(clip);
        if (audioUrl && !audioNodesRef.current.has(clip.id)) {
          clipsToLoad.push({ clip, audioUrl });
        }
      }
    }
    
    if (clipsToLoad.length === 0) return;
    
    setIsLoading(true);
    setLoadingProgress({ loaded: 0, total: clipsToLoad.length });
    
    const audioContext = getAudioContext();
    let loaded = 0;
    
    for (const { clip, audioUrl } of clipsToLoad) {
      const node = new AudioClipNode(audioContext, audioUrl);
      await node.loadAudio();
      audioNodesRef.current.set(clip.id, node);
      loaded++;
      setLoadingProgress({ loaded, total: clipsToLoad.length });
    }
    
    setIsLoading(false);
  }, [audioTracks, getAudioUrlForClip, getAudioContext]);

  // Preload audio when tracks change
  useEffect(() => {
    preloadAudioClips();
  }, [preloadAudioClips]);

  // Get or create audio node for a clip (should be preloaded, but fallback to loading)
  const getOrCreateAudioNode = useCallback(async (clip) => {
    if (audioNodesRef.current.has(clip.id)) {
      return audioNodesRef.current.get(clip.id);
    }

    const audioUrl = getAudioUrlForClip(clip);
    if (!audioUrl) return null;

    const audioContext = getAudioContext();
    const node = new AudioClipNode(audioContext, audioUrl);
    
    // Load the audio
    const loaded = await node.loadAudio();
    if (!loaded) return null;
    
    audioNodesRef.current.set(clip.id, node);
    return node;
  }, [getAudioUrlForClip, getAudioContext]);

  // Find all clips that should be playing at a given position
  const getActiveClips = useCallback((position) => {
    const activeClips = [];
    
    if (!audioTracks?.length) return activeClips;
    
    for (const track of audioTracks) {
      if (track.muted) continue;
      
      for (const clip of track.clips || []) {
        const clipStart = clip.start_ms;
        const clipDuration = getEffectiveDuration(clip);
        const clipEnd = clipStart + clipDuration;
        
        if (position >= clipStart && position < clipEnd) {
          activeClips.push({
            clip,
            track,
            clipStart,
            clipEnd,
            clipDuration
          });
        }
      }
    }
    
    return activeClips;
  }, [audioTracks]);

  // Stop all audio immediately
  const stopAllAudio = useCallback(() => {
    // Clear the sync interval first
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
    
    // Stop all nodes
    audioNodesRef.current.forEach((node) => {
      node.stop();
    });
    playingClipsRef.current.clear();
  }, []);

  // Handle play/pause state changes
  useEffect(() => {
    shouldPlayRef.current = isPlaying;
    
    if (!isPlaying) {
      // STOP immediately when paused
      stopAllAudio();
      return;
    }
    
    // Use positionRef if provided, otherwise fall back to local tracking
    const getRealtimePosition = () => {
      if (positionRef) return positionRef.current;
      return localPositionRef.current;
    };
    
    // Initialize local position from state when starting playback
    localPositionRef.current = playheadPosition;
    
    // Start playback sync
    const syncPlayback = async () => {
      // Check if we should still be playing (prevents race conditions)
      if (!shouldPlayRef.current) {
        stopAllAudio();
        return;
      }

      const currentPosition = getRealtimePosition();
      const activeClips = getActiveClips(currentPosition);
      const activeClipIds = new Set(activeClips.map(c => c.clip.id));

      // Stop clips that are no longer active
      playingClipsRef.current.forEach((clipId) => {
        if (!activeClipIds.has(clipId)) {
          const node = audioNodesRef.current.get(clipId);
          if (node) {
            node.stop();
          }
          playingClipsRef.current.delete(clipId);
        }
      });

      // Start/sync clips that should be playing
      for (const { clip, track, clipStart } of activeClips) {
        // Double-check we should still be playing
        if (!shouldPlayRef.current) {
          stopAllAudio();
          return;
        }
        
        const node = audioNodesRef.current.get(clip.id);
        if (!node || !node.audioBuffer) {
          // Try to load if not preloaded (shouldn't normally happen)
          const loadedNode = await getOrCreateAudioNode(clip);
          if (!loadedNode || !shouldPlayRef.current) continue;
        }
        
        const audioNode = audioNodesRef.current.get(clip.id);
        if (!audioNode || !audioNode.audioBuffer) continue;

        // Calculate where in the audio file we should be
        const positionInClip = currentPosition - clipStart;
        const trimStart = clip.trim_start_ms || 0;
        const speed = clip.settings?.speed ?? 1;
        
        // Convert timeline position to audio file position (seconds)
        const audioPosition = (trimStart + positionInClip * speed) / 1000;

        // Apply track and clip settings
        const trackVolumeDb = track.volume ?? 0;
        const clipVolumeDb = clip.settings?.volume ?? 0;
        const totalVolumeDb = Math.max(-60, Math.min(12, trackVolumeDb + clipVolumeDb));
        audioNode.setVolume(totalVolumeDb);
        
        const pan = clip.settings?.pan ?? 0;
        audioNode.setPan(pan);

        if (!playingClipsRef.current.has(clip.id)) {
          // New clip - start playing from position
          audioNode.play(audioPosition);
          playingClipsRef.current.add(clip.id);
        }
      }
    };

    // Initial sync
    syncPlayback();
    
    // Set up sync interval - runs every 100ms to check for clip boundaries
    syncIntervalRef.current = setInterval(syncPlayback, 100);
    
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      // Stop all audio when effect cleans up
      stopAllAudio();
    };
  }, [isPlaying, getActiveClips, getOrCreateAudioNode, stopAllAudio, playheadPosition, positionRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
      
      audioNodesRef.current.forEach((node) => {
        node.disconnect();
      });
      audioNodesRef.current.clear();
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [stopAllAudio]);

  return {
    getActiveClips,
    dbToGain,
    gainToDb,
    isLoading,
    loadingProgress,
    preloadAudioClips
  };
}

export default useAudioTrackPlayer;
